import settings from '@nobia/zeus-components/lib/settings'
import {
  activeStatusesFiltersNeedRepair,
  coerceToFilterStringArray,
  normalizeActiveStatusesSalesTool,
  sanitizeActiveDstStagesSalesTool,
  stripDeactivatedStatusFilters,
} from './compositeCustomerStatuses'
import { isMessagingEnabled } from './storeUtils'
import {
  SCROLL_POSITION_KEY,
  CUSTOMERS_DATA_KEY,
  PRESERVE_CUSTOMER_LISTING_KEY,
  CUSTOMER_LISTING_INPUT_KEY,
} from './constants'

/** Set on history.state when AppStoreSelect navigates to listing (e.g. search all stores). */
export const STORE_SELECT_LISTING_NAV_KEY = '__storeSelectNavigation'

export const stripStoreSelectNavigationMarker = (state) => {
  if (!state || typeof state !== 'object') {
    return state
  }
  const rest = { ...state }
  delete rest[STORE_SELECT_LISTING_NAV_KEY]
  return rest
}

export const FILTER_LOCAL_STORAGE_KEYS = [
  'sortingOption',
  'staffOption',
  'favoriteStaffOption',
  'favoriteSortingOption',
  'activeDstStagesSalesTool',
  'activeStatusesSalesTool',
  'favoriteActiveDstStagesSalesTool',
  'favoriteActiveStatusesSalesTool',
  'showInactive',
  'favoriteShowInactive',
]

export const LISTING_TAB_KEYS = {
  all: 'all',
  favourites: 'favourites',
}

export const FAVORITE_STAFF_OPTION_STORAGE_KEY = 'favoriteStaffOption'

const ALL_TAB_FILTER_STORAGE_KEYS = {
  sorting: 'sortingOption',
  staff: 'staffOption',
  activeDstStages: 'activeDstStagesSalesTool',
  activeStatuses: 'activeStatusesSalesTool',
  showInactive: 'showInactive',
}

const FAVOURITES_TAB_FILTER_STORAGE_KEYS = {
  sorting: 'favoriteSortingOption',
  staff: FAVORITE_STAFF_OPTION_STORAGE_KEY,
  activeDstStages: 'favoriteActiveDstStagesSalesTool',
  activeStatuses: 'favoriteActiveStatusesSalesTool',
  showInactive: 'favoriteShowInactive',
}

/** localStorage keys for status/stage/sort/staff/inactive per listing tab. */
export const getListingFilterStorageKeys = (tabKey) =>
  tabKey === LISTING_TAB_KEYS.favourites
    ? FAVOURITES_TAB_FILTER_STORAGE_KEYS
    : ALL_TAB_FILTER_STORAGE_KEYS

export const shouldPreserveCustomerListing = () =>
  sessionStorage.getItem(PRESERVE_CUSTOMER_LISTING_KEY) === 'true'

export const setPreserveCustomerListing = (preserve) => {
  if (preserve) {
    sessionStorage.setItem(PRESERVE_CUSTOMER_LISTING_KEY, 'true')
  } else {
    sessionStorage.removeItem(PRESERVE_CUSTOMER_LISTING_KEY)
  }
}

export const normalizeRoutePath = (path) => {
  if (!path || typeof path !== 'string') {
    return ''
  }
  const trimmed = path.trim()
  if (!trimmed) {
    return ''
  }
  const withLeading = trimmed.startsWith('/') ? trimmed : `/${trimmed}`
  return withLeading.replace(/\/+$/, '') || '/'
}

export const isCustomerListingPath = (pathname, customersPath = '/customers') =>
  normalizeRoutePath(pathname) === normalizeRoutePath(customersPath)

/**
 * Any route under /customers/:customer (detail and nested quote/edit/etc.).
 */
export const isCustomerDetailPath = (
  pathname,
  customersPath = '/customers'
) => {
  if (pathname === customersPath) {
    return false
  }
  if (!pathname.startsWith(`${customersPath}/`)) {
    return false
  }
  const rest = pathname.slice(customersPath.length + 1)
  const firstSegment = rest.split('/')[0]
  if (!firstSegment || firstSegment === 'create') {
    return false
  }
  return true
}

/**
 * Main customer detail only (/customers/:customer), not nested assign-store/quote/etc.
 */
export const isCustomerDetailMainPath = (
  pathname,
  customersPath = '/customers'
) => {
  if (pathname === customersPath) {
    return false
  }
  if (!pathname.startsWith(`${customersPath}/`)) {
    return false
  }
  const rest = pathname.slice(customersPath.length + 1)
  const segments = rest.split('/').filter(Boolean)
  if (segments.length !== 1) {
    return false
  }
  return segments[0] !== 'create'
}

const isListingInputShape = (state) =>
  state &&
  typeof state === 'object' &&
  typeof state.searchString === 'string' &&
  state.filter &&
  typeof state.filter === 'object' &&
  state.options &&
  typeof state.options === 'object'

const isTabListingInputsShape = (state) =>
  state &&
  typeof state === 'object' &&
  state.tabs &&
  typeof state.tabs === 'object' &&
  isListingInputShape(state.tabs[LISTING_TAB_KEYS.all]) &&
  isListingInputShape(state.tabs[LISTING_TAB_KEYS.favourites])

/** Removes corrupted filter arrays (e.g. activeStatusesSalesTool `[{}]` from Set serialization). */
export const repairCorruptedFilterLocalStorage = () => {
  const repairStatusesKey = (key) => {
    try {
      const raw = localStorage.getItem(key)
      if (!raw) {
        return
      }
      const parsed = JSON.parse(raw)
      if (activeStatusesFiltersNeedRepair(parsed)) {
        localStorage.setItem(
          key,
          JSON.stringify(normalizeActiveStatusesSalesTool(parsed))
        )
      }
    } catch {
      localStorage.removeItem(key)
    }
  }

  repairStatusesKey(ALL_TAB_FILTER_STORAGE_KEYS.activeStatuses)
  repairStatusesKey(FAVOURITES_TAB_FILTER_STORAGE_KEYS.activeStatuses)

  const repairDstStagesKey = (key) => {
    try {
      const raw = localStorage.getItem(key)
      if (!raw) {
        return
      }
      const parsed = JSON.parse(raw)
      const validDstStageIds = settings.search?.filter?.dstStages ?? []
      const cleaned = sanitizeActiveDstStagesSalesTool(parsed, validDstStageIds)
      const coerced = coerceToFilterStringArray(parsed)
      if (
        !Array.isArray(parsed) ||
        coerced.length !== cleaned.length ||
        coerced.some((value, index) => value !== cleaned[index])
      ) {
        localStorage.setItem(key, JSON.stringify(cleaned))
      }
    } catch {
      localStorage.removeItem(key)
    }
  }

  repairDstStagesKey(ALL_TAB_FILTER_STORAGE_KEYS.activeDstStages)
  repairDstStagesKey(FAVOURITES_TAB_FILTER_STORAGE_KEYS.activeDstStages)
}

export const readStoredCustomersData = () => {
  try {
    const raw = sessionStorage.getItem(CUSTOMERS_DATA_KEY)
    if (!raw) {
      return null
    }
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed) ? parsed : null
  } catch {
    return null
  }
}

export const clearFilterLocalStorage = () => {
  FILTER_LOCAL_STORAGE_KEYS.forEach((key) => localStorage.removeItem(key))
}

export const clearStoredListingState = () => {
  sessionStorage.removeItem(PRESERVE_CUSTOMER_LISTING_KEY)
  sessionStorage.removeItem(CUSTOMER_LISTING_INPUT_KEY)
  sessionStorage.removeItem(CUSTOMERS_DATA_KEY)
  sessionStorage.removeItem(SCROLL_POSITION_KEY)
}

const filterArraysEqual = (a, b) => {
  const left = Array.isArray(a) ? a : []
  const right = Array.isArray(b) ? b : []
  return left.length === right.length && left.every((v, i) => v === right[i])
}

const sanitizeListingFilter = (filter) => {
  if (!filter) {
    return filter
  }

  const validDstStageIds = settings.search?.filter?.dstStages ?? []
  const dstStages = sanitizeActiveDstStagesSalesTool(
    filter.activeDstStagesSalesTool,
    validDstStageIds
  )
  const rawStatuses = filter.activeStatusesSalesTool || []
  const normalizedStatuses = normalizeActiveStatusesSalesTool(rawStatuses)
  const statuses = !filter.showInactive
    ? stripDeactivatedStatusFilters(normalizedStatuses)
    : normalizedStatuses

  const dstChanged = !filterArraysEqual(dstStages, filter.activeDstStagesSalesTool)
  const statusChanged = !filterArraysEqual(statuses, filter.activeStatusesSalesTool)

  if (!dstChanged && !statusChanged) {
    return filter
  }

  return {
    ...filter,
    ...(dstChanged && { activeDstStagesSalesTool: dstStages }),
    ...(statusChanged && { activeStatusesSalesTool: statuses }),
  }
}

/** True when Magnet "Search all stores" is selected (empty store filter). */
export const isSearchAllStoresListingFilter = (storeId) =>
  storeId === '' || storeId == null

/**
 * Cross-store search must not scope by staff; designers may still have staffId in UI state.
 */
export const withListingSearchStaffFilterForStore = (input) => {
  if (!input?.filter || !isSearchAllStoresListingFilter(input.filter.storeId)) {
    return input
  }
  if (!input.filter.staffId) {
    return input
  }
  return {
    ...input,
    filter: {
      ...input.filter,
      staffId: '',
    },
  }
}

/**
 * Maps favourites designer UI value to searchFavoriteCustomers filter fields.
 * - "my" / email → favoriteStaffEmail (staffId omitted)
 * - "all" → staffId: "" (favoriteStaffEmail omitted)
 */
export const buildFavoriteListingSearchInput = (
  baseInput,
  favoriteDesignerValue,
  profileEmail
) => {
  if (!baseInput?.filter) {
    return baseInput
  }

  const filter = { ...baseInput.filter }
  delete filter.staffId
  delete filter.favoriteStaffEmail

  if (favoriteDesignerValue === 'all') {
    filter.staffId = ''
  } else {
    const email =
      favoriteDesignerValue === 'my'
        ? profileEmail?.toLowerCase?.() || profileEmail || ''
        : favoriteDesignerValue
    if (email) {
      filter.favoriteStaffEmail = email
    }
  }

  return withListingSearchStaffFilterForStore({
    ...baseInput,
    filter,
  })
}

const withSanitizedFilter = (input, storeId) =>
  withListingSearchStaffFilterForStore({
    ...input,
    filter: sanitizeListingFilter({
      ...input.filter,
      storeId: input.filter.storeId ?? storeId ?? '',
    }),
  })

export const getDefaultListingSort = ({ isMagnetBrand, storeId }) => {
  if (
    isMagnetBrand &&
    isMessagingEnabled(storeId) &&
    settings.store.multiple !== true
  ) {
    return { field: 'LATEST_MESSAGE_DATE', order: 'DESC' }
  }
  return { field: 'LAST_ACTION_DATE', order: 'DESC' }
}

/** Default sort for MultipleCustomersMessages inbox (not customer listing). */
export const getDefaultInboxSort = ({ isMagnetBrand, storeId }) => {
  if (
    isMagnetBrand &&
    isMessagingEnabled(storeId) &&
    settings.store.multiple !== true
  ) {
    return { field: 'LAST_ACTIVITY_MESSAGE_AT', order: 'DESC' }
  }
  return getDefaultListingSort({ isMagnetBrand, storeId })
}

export const getDefaultListingInput = ({ storeId, isMagnetBrand }) => {
  const { field, order } = getDefaultListingSort({
    isMagnetBrand,
    storeId,
  })
  return {
    searchString: '',
    filter: {
      storeId: storeId ?? '',
      showInactive: false,
      activeDstStagesSalesTool: [],
      activeStatusesSalesTool: [],
      staffId: '',
    },
    options: {
      field,
      order,
      ...(isMagnetBrand && { allowSearchWithoutStoreId: true }),
    },
  }
}

export const getDefaultTabListingInputs = ({ storeId, isMagnetBrand }) => {
  const sharedStoreId = storeId ?? ''
  const all = getDefaultListingInput({ storeId: sharedStoreId, isMagnetBrand })
  const favourites = getDefaultListingInput({
    storeId: sharedStoreId,
    isMagnetBrand,
  })
  return {
    [LISTING_TAB_KEYS.all]: all,
    [LISTING_TAB_KEYS.favourites]: favourites,
  }
}

const normalizeTabListingInputs = (tabs, storeId) => ({
  [LISTING_TAB_KEYS.all]: withSanitizedFilter(
    tabs[LISTING_TAB_KEYS.all],
    storeId ?? tabs[LISTING_TAB_KEYS.all]?.filter?.storeId
  ),
  [LISTING_TAB_KEYS.favourites]: withSanitizedFilter(
    tabs[LISTING_TAB_KEYS.favourites],
    storeId ?? tabs[LISTING_TAB_KEYS.favourites]?.filter?.storeId
  ),
})

const flatInputToTabInputs = (input, storeId, isMagnetBrand = true) => {
  const sanitized = withSanitizedFilter(input, storeId ?? input.filter?.storeId)
  const favouritesDefault = getDefaultListingInput({
    storeId: sanitized.filter?.storeId ?? storeId,
    isMagnetBrand,
  })
  return normalizeTabListingInputs(
    {
      [LISTING_TAB_KEYS.all]: sanitized,
      [LISTING_TAB_KEYS.favourites]: {
        ...favouritesDefault,
        filter: {
          ...favouritesDefault.filter,
          storeId: sanitized.filter?.storeId ?? storeId ?? '',
        },
      },
    },
    storeId
  )
}

/** @deprecated Use readStoredTabListingInputs for Magnet dual-tab state. */
export const readStoredListingInput = () => {
  const stored = readStoredTabListingInputs()
  if (!stored) {
    return null
  }
  return stored.tabs[LISTING_TAB_KEYS.all]
}

export const readStoredTabListingInputs = () => {
  try {
    const raw = sessionStorage.getItem(CUSTOMER_LISTING_INPUT_KEY)
    if (!raw) {
      return null
    }
    const parsed = JSON.parse(raw)
    if (isTabListingInputsShape(parsed)) {
      const storeId =
        parsed.tabs[LISTING_TAB_KEYS.all]?.filter?.storeId ??
        parsed.tabs[LISTING_TAB_KEYS.favourites]?.filter?.storeId
      return {
        activeTab: parsed.activeTab ?? LISTING_TAB_KEYS.all,
        tabs: normalizeTabListingInputs(parsed.tabs, storeId),
      }
    }
    if (isListingInputShape(parsed)) {
      const storeId = parsed.filter?.storeId
      return {
        activeTab: LISTING_TAB_KEYS.all,
        tabs: flatInputToTabInputs(parsed, storeId),
      }
    }
    return null
  } catch {
    return null
  }
}

/** @deprecated Use writeStoredTabListingInputs for Magnet dual-tab state. */
export const writeStoredListingInput = (input) => {
  if (!isListingInputShape(input)) {
    return
  }
  writeStoredTabListingInputs({
    activeTab: LISTING_TAB_KEYS.all,
    tabs: flatInputToTabInputs(input, input.filter?.storeId),
  })
}

export const writeStoredTabListingInputs = ({ activeTab, tabs }) => {
  if (!tabs || !isListingInputShape(tabs[LISTING_TAB_KEYS.all])) {
    return
  }
  const storeId =
    tabs[LISTING_TAB_KEYS.all]?.filter?.storeId ??
    tabs[LISTING_TAB_KEYS.favourites]?.filter?.storeId
  const normalizedTabs = normalizeTabListingInputs(tabs, storeId)
  sessionStorage.setItem(
    CUSTOMER_LISTING_INPUT_KEY,
    JSON.stringify({
      activeTab: activeTab ?? LISTING_TAB_KEYS.all,
      tabs: normalizedTabs,
    })
  )
}

/**
 * Builds sanitized GraphQL listing input from a single tab's raw input state.
 */
export const buildListingSearchInput = (input, validDstStageIds) => {
  if (!input) {
    return input
  }
  return withListingSearchStaffFilterForStore({
    ...input,
    filter: {
      ...input.filter,
      activeDstStagesSalesTool: sanitizeActiveDstStagesSalesTool(
        input.filter.activeDstStagesSalesTool,
        validDstStageIds
      ),
      activeStatusesSalesTool: normalizeActiveStatusesSalesTool(
        input.filter.activeStatusesSalesTool
      ),
    },
    options: {
      ...input.options,
      ...(isSearchAllStoresListingFilter(input.filter.storeId) && {
        allowSearchWithoutStoreId: true,
      }),
    },
  })
}

/**
 * Resolves initial listing input on mount.
 */
export const resolveInitialListingInput = ({
  preserve,
  locationState,
  storeId,
  isMagnetBrand,
}) => {
  const tabState = resolveInitialTabListingInputs({
    preserve,
    locationState,
    storeId,
    isMagnetBrand,
  })
  return tabState.tabs[LISTING_TAB_KEYS.all]
}

/**
 * Resolves dual-tab listing inputs on mount (Magnet All + Favourites).
 */
export const resolveInitialTabListingInputs = ({
  preserve,
  locationState,
  storeId,
  isMagnetBrand,
}) => {
  const defaultTabs = getDefaultTabListingInputs({ storeId, isMagnetBrand })

  if (!preserve) {
    if (
      isListingInputShape(locationState) &&
      locationState[STORE_SELECT_LISTING_NAV_KEY] === true
    ) {
      const input = stripStoreSelectNavigationMarker(locationState)
      return {
        activeTab: LISTING_TAB_KEYS.all,
        tabs: flatInputToTabInputs(input, storeId, isMagnetBrand),
      }
    }
    return {
      activeTab: LISTING_TAB_KEYS.all,
      tabs: defaultTabs,
    }
  }

  const stored = readStoredTabListingInputs()
  if (stored) {
    return {
      activeTab: stored.activeTab ?? LISTING_TAB_KEYS.all,
      tabs: normalizeTabListingInputs(stored.tabs, storeId),
    }
  }

  if (isTabListingInputsShape(locationState)) {
    return {
      activeTab: locationState.activeTab ?? LISTING_TAB_KEYS.all,
      tabs: normalizeTabListingInputs(locationState.tabs, storeId),
    }
  }

  if (isListingInputShape(locationState)) {
    return {
      activeTab: LISTING_TAB_KEYS.all,
      tabs: flatInputToTabInputs(locationState, storeId, isMagnetBrand),
    }
  }

  return {
    activeTab: LISTING_TAB_KEYS.all,
    tabs: defaultTabs,
  }
}
